import { Divider } from 'antd'
import Input from 'components/Input'
import { InputFieldTypes } from 'components/Input/types'
import Pagination from 'components/Pagination'
import { getTotalPages } from 'components/Pagination/helpers'
import PerPage from 'components/PerPage'
import { I18nContext } from 'contexts/I18n/I18nProvider'
import NoRecords from 'core/no-records/no-records-container'
import { ConfigManagementLogOperation, ConfigManagementLogsState, ConfigManagementLogStatus, GetConfigManagementLogsFunction } from 'modules/operational/rtf/redux/logs/configManagement/types'
import { useContext, useState } from 'react'
import Field from '../common/Field'
import GridContainer from '../common/GridContainer'
import LogEntry from './LogEntry'

const STATUS_OPTIONS = [
  {
    label: '',
    value: '',
  },
  {
    label: 'command.label.success',
    value: ConfigManagementLogStatus.success,
  },
  {
    label: 'command.label.failed',
    value: ConfigManagementLogStatus.failed,
  },
]

const OPERATION_OPTIONS = [
  {
    label: '',
    value: '',
  },
  {
    label: 'general.upload',
    value: ConfigManagementLogOperation.upload,
  },
  {
    label: 'general.delete',
    value: ConfigManagementLogOperation.delete,
  },
]

type ConfigManagementLogProps = {
  configManagementLogs: ConfigManagementLogsState
  getConfigManagementUploadLogs: GetConfigManagementLogsFunction
}

export default function ConfigManagementLogComponent({ configManagementLogs, getConfigManagementUploadLogs }: ConfigManagementLogProps) {
  const t = useContext(I18nContext)

  const [fileName, setFileName] = useState('')
  const [user, setUser] = useState('')
  const [status, setStatus] = useState<string>(STATUS_OPTIONS[0].value)
  const [operation, setOperation] = useState<string>(OPERATION_OPTIONS[0].value)
  const initialEventDateValue = { start: '', end: '' }
  const [eventDate, setEventDate] = useState(initialEventDateValue)
  const { items, totalElements, filter, page, pageSize } = configManagementLogs

  const isDirty = () => {
    return fileName || user || status || operation || eventDate.start || eventDate.end
  }

  const clearFilters = () => {
    setFileName('')
    setUser('')
    setStatus('')
    setOperation('')
    setEventDate(initialEventDateValue)
  }

  const onSearch = () => {
    getConfigManagementUploadLogs({
      resourceName: fileName,
      status,
      operation,
      createdBy: user,
      createdDateFrom: eventDate.start,
      createdDateTo: eventDate.end,
    }, 1, pageSize)
  }

  const setPage = (newPage: number) => getConfigManagementUploadLogs(filter, newPage, pageSize)
  const setPageSize = (newPageSize: number) => getConfigManagementUploadLogs(filter, 1, newPageSize)

  return (
    <GridContainer>
      <Field>
        <div className="rtf-config-management-logs-search-header">
          <div>
            <Input
              type={InputFieldTypes.text}
              value={fileName}
              setValue={setFileName}
              labelParams={{
                i18nTag: 'rtf.configuration_management_logs_file_name',
                onClean: () => setFileName('')
              }}
            />
          </div>
          <div>
            <Input
              type={InputFieldTypes.select}
              value={status}
              setValue={setStatus}
              options={STATUS_OPTIONS}
              labelParams={{
                i18nTag: 'rtf.configuration_management_logs_status',
                onClean: () => setStatus(STATUS_OPTIONS[0].value)
              }}
            />
          </div>
          <div>
            <Input
              type={InputFieldTypes.text}
              value={user}
              setValue={setUser}
              labelParams={{
                i18nTag: 'rtf.configuration_management_logs_user',
                onClean: () => setUser('')
              }}
            />
          </div>
          <div>
            <Input
              type={InputFieldTypes.select}
              value={operation}
              setValue={setOperation}
              options={OPERATION_OPTIONS}
              labelParams={{
                i18nTag: 'rtf.configuration_management_logs_operation',
                onClean: () => setOperation(OPERATION_OPTIONS[0].value)
              }}
            />
          </div>
          <div className="config-management-log-search-date-input">
            <Input
              type={InputFieldTypes.datePicker}
              value={eventDate}
              setValue={setEventDate}
              labelParams={{
                i18nTag: 'command.field_lastManaged',
                onClean: () => setEventDate(initialEventDateValue)
              }}
            />
          </div>
          <div className="config-management-log-search-button">
            <button
              className="button is-larger is-primary"
              onClick={onSearch}
            >
              {t('general.search')}
            </button>
            <button
              className={`button-link dropdown-link has-padding-right-md ${isDirty() ? 'button-action' : ''}`}
              disabled={!isDirty()}
              onClick={() => clearFilters()}
            >
              {t('general.clear_filters')}
            </button>
          </div>
        </div>
      </Field>
      <Field>
        <Divider/>
        {items.length ?
          <div className="config-management-log-tasks-container">
            <PerPage pageSize={pageSize} setPageSize={setPageSize} />
            <ul className="panel has-padding-top-xs">
              {items.map((item, index) => (
                <LogEntry key={`${page}-${index}`} entry={item}/>))
              }
            </ul>
            <Pagination page={page} setPage={setPage} totalPages={getTotalPages(totalElements, pageSize)} />
          </div>
          :
          <NoRecords />
        }
      </Field>
    </GridContainer>
  )
}
